/**
 * BudgetProgressCard — the per-client Budget card on the dashboards tab
 * (Story 18.2 AC #6).
 *
 * Renders "Spent $X of $Y (Z% used)" progress against each configured budget,
 * sourced from GET /api/clients/:id/budget-pacing (useClientBudgetPacing — the
 * server resolves demo/live spend; the web never computes pacing locally). The
 * headline TOTAL bar (the 'all' sentinel) sits on top with "N days remaining";
 * per-platform bars sit beneath. Bars are hand-rolled divs (the GoalWidget
 * precedent — there is NO Progress primitive in packages/ui).
 *
 * Threshold colors mirror the goal-widget convention (warning 80 / danger 100):
 *  < 80% used → brand/navy fill · ≥ 80% → amber/warning · ≥ 100% → destructive.
 *  A platform whose spend fetch failed reports spend === null upstream — but the
 *  pacing route never nulls a row, it flags `incomplete`; an incomplete row
 *  renders the bar empty with a muted "Spend unavailable" hint (no red chrome).
 *
 * Empty state (no budgets): a short pitch + brand "Set budgets" button. When
 * budgets exist, a quiet "Edit budgets" affordance (Phosphor PencilSimple)
 * opens the same dialog. The dialog is ALWAYS-MOUNTED (§4.6).
 *
 * Card chrome per §7 (rounded-[22px], 0.5px inline border, shadow-xs); header
 * Phosphor CurrencyDollar icon + the matching text-lg title. Phosphor only.
 */

import { useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import { Button } from "@provenleap/ui";
import { CurrencyDollar, PencilSimple, Wallet } from "@phosphor-icons/react";
import {
  useClientBudgets,
  useClientBudgetPacing,
  type ClientBudgetPacingEntry,
} from "../../hooks/useClientBudgets";
import { platformBadge } from "../../lib/integrations";
import { BudgetConfigDialog } from "./BudgetConfigDialog";
import type { ToastType } from "../../hooks/useToast";

interface BudgetProgressCardProps {
  clientId: string;
  clientName?: string;
  showToast: (type: ToastType, message: string) => void;
}

const CARD_BORDER = "0.5px solid var(--hairline)";

// Threshold breakpoints — goalWidgetConfigSchema defaults {warning:80,danger:100}
// (report-layout-types.ts). < 80 navy · ≥ 80 amber · ≥ 100 red.
const THRESHOLD_WARNING = 80;
const THRESHOLD_DANGER = 100;

const usdFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 0,
});

/** "$5,000" — USD, no decimals (the formatGoalValue precedent). */
function formatUsd(value: number): string {
  return usdFormatter.format(value);
}

/** Clamps a percentage to [0,100] for the bar FILL only (label may exceed 100). */
function clampBarFill(pct: number): number {
  if (Number.isNaN(pct)) return 0;
  return Math.max(0, Math.min(100, pct));
}

/** Tailwind fill class by percent-used threshold. */
function barFillClass(percentUsed: number): string {
  if (percentUsed >= THRESHOLD_DANGER) return "bg-red-500";
  if (percentUsed >= THRESHOLD_WARNING) return "bg-amber-500";
  return "bg-blue-600";
}

/** Days from now (exclusive) to the period end (unix ms), floored at 0. */
function daysRemaining(periodEnd: number): number {
  const ms = periodEnd - Date.now();
  if (ms <= 0) return 0;
  return Math.ceil(ms / (24 * 60 * 60 * 1000));
}

// ============================================================================
// One progress bar (total or per-platform)
// ============================================================================

function BudgetBar({
  label,
  logoSrc,
  entry,
  showDaysRemaining,
  isTotal,
}: {
  label: string;
  logoSrc: string | null;
  entry: ClientBudgetPacingEntry;
  showDaysRemaining: boolean;
  isTotal: boolean;
}) {
  const percentUsed = Math.round(entry.spentPct * 10) / 10;
  const remaining = daysRemaining(entry.periodEnd);

  return (
    <div
      className="py-3"
      data-testid={isTotal ? "budget-total-bar" : "budget-platform-bar"}
      data-platform={entry.integrationType}
    >
      <div className="flex items-center gap-2">
        {logoSrc ? (
          <img
            src={logoSrc}
            alt=""
            title={label}
            className="h-4 w-4 shrink-0 rounded-sm"
          />
        ) : isTotal ? (
          <Wallet size={16} className="shrink-0 text-foreground" />
        ) : null}
        <span
          className={`truncate ${isTotal ? "text-sm font-semibold text-foreground" : "text-[13px] font-medium text-foreground"}`}
        >
          {label}
        </span>
      </div>

      {/* Progress bar. */}
      <div
        className="relative mt-2 h-2 w-full rounded-full bg-muted"
        aria-hidden="true"
      >
        {!entry.incomplete && (
          <div
            data-testid="budget-bar-fill"
            data-threshold={barFillClass(percentUsed)}
            className={`h-full rounded-full transition-all duration-500 ${barFillClass(percentUsed)}`}
            style={{ width: `${clampBarFill(entry.spentPct)}%` }}
          />
        )}
      </div>

      {/* Spent / of / percent line — the literal epic format. */}
      {entry.incomplete ? (
        <p
          className="mt-1.5 text-[13px] text-muted-foreground"
          title="One or more platforms could not be reached"
          data-testid="budget-unavailable"
        >
          Spend unavailable — {formatUsd(entry.amount)} budget
        </p>
      ) : (
        <p className="mt-1.5 text-[13px] font-medium text-foreground">
          Spent {formatUsd(entry.spend)} of {formatUsd(entry.amount)} ({percentUsed}% used)
        </p>
      )}

      {showDaysRemaining && (
        <p className="mt-0.5 text-[12px] text-muted-foreground">
          {remaining} days remaining
        </p>
      )}
    </div>
  );
}

// ============================================================================
// Component
// ============================================================================

export function BudgetProgressCard({
  clientId,
  clientName,
  showToast,
}: BudgetProgressCardProps) {
  const { budgets, isLoading: budgetsLoading } = useClientBudgets(clientId);
  const { pacing, isLoading: pacingLoading } = useClientBudgetPacing(clientId);
  const [dialogOpen, setDialogOpen] = useState(false);

  const hasBudgets = budgets.length > 0;
  const isLoading = budgetsLoading || pacingLoading;

  const total = pacing.find((p) => p.integrationType === "all") ?? null;
  const platformEntries = pacing
    .filter((p) => p.integrationType !== "all")
    .sort((a, b) => a.integrationType.localeCompare(b.integrationType));

  return (
    <div
      className="!rounded-[22px] bg-background p-5 shadow-xs"
      style={{ border: CARD_BORDER }}
      data-testid="budget-progress-card"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <CurrencyDollar size={18} className="text-foreground" />
          <h3 className="text-lg font-semibold text-foreground">Budgets</h3>
        </div>
        {hasBudgets && (
          <button
            type="button"
            data-testid="budget-edit"
            aria-label="Edit budgets"
            onClick={() => setDialogOpen(true)}
            className="flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-[13px] text-muted-foreground transition-colors hover:bg-accent hover:text-foreground cursor-pointer"
          >
            <PencilSimple size={14} />
            Edit budgets
          </button>
        )}
      </div>

      <div className="mt-4">
        {isLoading ? (
          <div className="space-y-4" data-testid="budget-loading">
            {[0, 1].map((i) => (
              <div key={i} className="space-y-2">
                <div className="h-4 w-1/3 animate-pulse rounded bg-muted" />
                <div className="h-2 w-full animate-pulse rounded-full bg-muted" />
                <div className="h-3 w-1/2 animate-pulse rounded bg-muted" />
              </div>
            ))}
          </div>
        ) : !hasBudgets ? (
          <div
            className="flex flex-col items-center justify-center py-10 text-center"
            data-testid="budget-empty"
          >
            <div className="mb-3 rounded-full bg-muted p-3">
              <Wallet size={28} className="text-muted-foreground" />
            </div>
            <p className="mb-3 text-sm text-muted-foreground">
              No budgets yet — set a monthly or quarterly limit to track spend
            </p>
            <Button
              variant="brand"
              className="rounded-2xl text-[13px] cursor-pointer"
              data-testid="budget-empty-cta"
              onClick={() => setDialogOpen(true)}
            >
              Set budgets
            </Button>
          </div>
        ) : (
          <div className="divide-y-[0.5px] divide-input/70">
            {/* Headline total bar (when a total is configured). */}
            {total && (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.05 }}
              >
                <BudgetBar
                  label="Total ad spend"
                  logoSrc={null}
                  entry={total}
                  showDaysRemaining
                  isTotal
                />
              </motion.div>
            )}

            {/* Per-platform bars. */}
            <AnimatePresence initial={false}>
              {platformEntries.map((entry, index) => {
                const badge = platformBadge(entry.integrationType);
                return (
                  <motion.div
                    key={entry.budgetId}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.05 * (index + (total ? 2 : 1)) }}
                  >
                    <BudgetBar
                      label={badge?.displayName ?? entry.integrationType}
                      logoSrc={badge?.logoSrc ?? null}
                      entry={entry}
                      showDaysRemaining={!total}
                      isTotal={false}
                    />
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </div>
        )}
      </div>

      {/* Always-mounted (§4.6). */}
      <BudgetConfigDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        clientId={clientId}
        clientName={clientName}
        showToast={showToast}
      />
    </div>
  );
}
